'use client';

import * as React from 'react';

import { APP_NAME } from '@workspace/common/app';
import { cn } from '@workspace/ui/lib/utils';

/**
 * Brand masters live in /brand — change the logo there first, then regenerate
 * the path data below. Do not hand-edit these strings.
 *
 * Mark:     brand/svg/lumnis-mark.svg      (Bevel L, 56x56 artboard)
 * Wordmark: brand/svg/lumnis-wordmark.svg  ("lumnis" in Affinik, outlined,
 *           tracking -0.012em). Outlined on purpose: the logo carries no
 *           webfont dependency, so the UI font stays DM Sans.
 */
const MARK_VIEW_BOX = '0 0 56 56';
const MARK_PATH = 'M8 4 H23 V37 H52 L37 52 H8 Z';

const WORDMARK_VIEW_BOX = '0 0 2835 760';
const WORDMARK_PATH =
  'M0.0 745C0.0 748 2.0 750 5.0 750H140.0C143.0 750 145.0 748 145.0 745V5C145.0 2 143.0 0 140.0 0H5.0C2.0 0 0.0 2 0.0 5ZM544.0 745C543.0 748 544.0 750 547.0 750H683.0C686.0 750 688.0 748 688.0 745V255C688.0 252 686.0 250 683.0 250H548.0C545.0 250 543.0 252 543.0 255V533C543.0 598 492.0 647 423.0 647C376.0 647 353.0 624 353.0 577V255C353.0 252 351.0 250 348.0 250H213.0C210.0 250 208.0 252 208.0 255V573C208.0 685 273.0 760 371.0 760C451.0 760 528.0 710 576.0 619H587.0ZM1426.0 745C1426.0 748 1428.0 750 1431.0 750H1566.0C1569.0 750 1571.0 748 1571.0 745V427C1571.0 315 1506.0 240 1408.0 240C1339.0 240 1274.0 276 1226.0 343C1224.0 346 1222.0 345 1221.0 342C1197.0 279 1143.0 240 1073.0 240C993.0 240 916.0 290 868.0 381H857.0L900.0 255C901.0 252 900.0 250 897.0 250H761.0C758.0 250 756.0 252 756.0 255V745C756.0 748 758.0 750 761.0 750H896.0C899.0 750 901.0 748 901.0 745V467C901.0 402 952.0 353 1021.0 353C1068.0 353 1091.0 376 1091.0 423V745C1091.0 748 1093.0 750 1096.0 750H1231.0C1234.0 750 1236.0 748 1236.0 745V467C1236.0 402 1287.0 353 1356.0 353C1403.0 353 1426.0 376 1426.0 423ZM1778.0 255C1779.0 252 1778.0 250 1775.0 250H1639.0C1636.0 250 1634.0 252 1634.0 255V745C1634.0 748 1636.0 750 1639.0 750H1774.0C1777.0 750 1779.0 748 1779.0 745V467C1779.0 402 1830.0 353 1899.0 353C1946.0 353 1969.0 376 1969.0 423V745C1969.0 748 1971.0 750 1974.0 750H2109.0C2112.0 750 2114.0 748 2114.0 745V427C2114.0 315 2049.0 240 1951.0 240C1871.0 240 1794.0 290 1746.0 381H1735.0ZM2177.0 745C2177.0 748 2179.0 750 2182.0 750H2317.0C2320.0 750 2322.0 748 2322.0 745V255C2322.0 252 2320.0 250 2317.0 250H2182.0C2179.0 250 2177.0 252 2177.0 255ZM2177.0 122C2177.0 163 2209.0 195 2250.0 195C2290.0 195 2322.0 163 2322.0 122C2322.0 82 2290.0 50 2250.0 50C2209.0 50 2177.0 82 2177.0 122ZM2695.0 413C2696.0 416 2698.0 417 2701.0 416L2802.0 370C2805.0 369 2806.0 366 2806.0 363C2796.0 293 2714.0 240 2590.0 240C2469.0 240 2381.0 290 2381.0 371C2381.0 526 2703.0 570 2703.0 644C2703.0 670 2665.0 690 2618.0 690C2543.0 690 2502.0 638 2499.0 584C2499.0 581 2497.0 579 2494.0 580L2365.0 610C2362.0 611 2360.0 613 2361.0 616C2381.0 699 2464.0 760 2608.0 760C2740.0 760 2835.0 709 2835.0 621C2835.0 457 2510.0 407 2510.0 343C2510.0 325 2535.0 310 2573.0 310C2634.0 310 2675.0 348 2695.0 413Z';

/**
 * Everything is sized in `em`, so font-size is the single knob for the whole
 * lockup and the mark can never drift out of proportion with the wordmark.
 * At the nav's 20px that yields a 28px tile and a 15.2px wordmark.
 *
 *   WORDMARK_HEIGHT  0.76em  glyph ink box (ascender to overshoot) over the
 *                            font's 1000 unit em.
 *   TILE_SIZE        1.4em   the rounded symbol tile.
 *   MARK_SIZE      0.802em   = TILE_SIZE x 0.45 x (56/44). The mark's ink spans
 *                            44 of its 56 unit artboard, and brand spec 5e puts
 *                            it at 45% of tile width. See brand/README.md.
 *   GAP              0.4em   mark to wordmark.
 */
const WORDMARK_HEIGHT = 'h-[0.76em]';
const TILE_SIZE = 'size-[1.4em]';
const MARK_SIZE = 'size-[0.802em]';
const GAP = 'gap-[0.4em]';

export type LogoElement = React.ComponentRef<'div'>;
export type LogoProps = React.ComponentPropsWithoutRef<'div'> & {
  hideSymbol?: boolean;
  hideWordmark?: boolean;
};
export function Logo({
  hideSymbol,
  hideWordmark,
  className,
  ...other
}: LogoProps): React.JSX.Element {
  return (
    <div
      role="img"
      aria-label={APP_NAME}
      className={cn('flex items-center', GAP, className)}
      {...other}
    >
      {!hideSymbol && (
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md border bg-primary text-primary-foreground',
            TILE_SIZE
          )}
        >
          <svg
            viewBox={MARK_VIEW_BOX}
            className={MARK_SIZE}
            fill="none"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d={MARK_PATH}
              fill="currentColor"
            />
          </svg>
        </div>
      )}
      {!hideWordmark && (
        <svg
          viewBox={WORDMARK_VIEW_BOX}
          className={cn(WORDMARK_HEIGHT, 'w-auto shrink-0')}
          fill="currentColor"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d={WORDMARK_PATH} />
        </svg>
      )}
    </div>
  );
}
